import React, { useState, useEffect } from 'react';
import { adminApi, mainAdminApi } from '../../config';
import toast from 'react-hot-toast';
import { Loader2, ShieldAlert } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SubRoleSelectorProps {
  vendorId: string;
  vendorRole: string;
  vendorSubRole?: string | null;
  onSuccess: () => void;
}

const SubRoleSelector: React.FC<SubRoleSelectorProps> = ({ vendorId, vendorRole, vendorSubRole, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [subRoles, setSubRoles] = useState<any[]>([]);
  const [selectedSubRole, setSelectedSubRole] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // If vendor already has a subRole, or missing critical info, don't show
    if (vendorSubRole || !vendorId || !vendorRole) {
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    const fetchCategories = async () => {
      try {
        const response = await mainAdminApi.get<{ categories: any[] }>('/signup-categories');
        const categories = response.data.categories || [];
        
        const matchedCategory = categories.find((c: any) => c.name.toLowerCase() === vendorRole.toLowerCase());
        
        if (matchedCategory && matchedCategory.subCategories) {
          const activeSubs = matchedCategory.subCategories.filter((sub: any) => sub.isActive !== false);
          if (activeSubs.length > 0) {
            setSubRoles(activeSubs);
            setIsOpen(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch signup categories:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, [vendorId, vendorRole, vendorSubRole]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubRole) {
      toast.error('Please select a sub-role to continue');
      return;
    }

    setIsSubmitting(true);
    try {
      // Determine correct URL/host if adminApi/mainAdminApi points somewhere specific
      // We'll use the generic api for the vendor backend
      // Adjusting endpoint depending on configuration:
      // Note: `update-vendor-subrole` is on the mentor routes in metafit-services
      const apiEndpoint = adminApi; // Usually points to metafit-services /api/v1/mentor/
      
      const response = await apiEndpoint.put<{success: boolean; message?: string}>('/update-vendor-subrole', {
        mentorId: vendorId,
        role: vendorRole,
        subRole: selectedSubRole
      });

      if (response.data.success) {
        toast.success('Sub-role updated successfully!');
        setIsOpen(false);
        onSuccess();
      } else {
        toast.error(response.data.message || 'Failed to update sub-role');
      }
    } catch (error: any) {
      console.error('Update sub-role error:', error);
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShieldAlert className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Important Update</h2>
            <p className="text-sm text-gray-500 mt-2">
              Your primary role as <strong>{vendorRole}</strong> requires you to specify a sub-specialty before you can continue using the dashboard.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Your Sub-Role *
              </label>
              <select
                required
                value={selectedSubRole}
                onChange={(e) => setSelectedSubRole(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
              >
                <option value="" disabled>Select a sub-role...</option>
                {subRoles.map((sub: any) => (
                  <option key={sub._id || sub.name} value={sub.name}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </div>

            <button 
              disabled={isSubmitting || !selectedSubRole}
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-bold shadow-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                  <><Loader2 className="animate-spin" size={18} /> Updating...</>
              ) : (
                  "Save & Continue"
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SubRoleSelector;
